import DashboardCard from "@/components/dashboard-card";
import DonutChart from "@/components/donut-chart";
import { OrderLineChart } from "@/components/line-chart";
import LoadingSpinner from "@/components/orders/LoadingSpinner";
import TimeWidget from "@/components/time-widget";
import { StateMetrics } from "@/hooks/useOrdersApi";
import { getDonutData } from "@/lib/statusConfig";
import { useState, useMemo, useCallback } from "react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/atoms/select";

const OrdersDashboard = ({
  loading,
  stateMetrics = [],
  dailyBreakdown,
  totalOrders,
}: {
  loading: boolean;
  stateMetrics?: StateMetrics[];
  dailyBreakdown?: {
    date: string;
    totalOrders: number;
    completedOrders: number;
    avgCompletionTime: number;
  }[];
  totalOrders: number;
}) => {
  const getTimeData = (status: string) => {
    const metric = stateMetrics?.find((m) => m.status === status);

    if (!metric || !metric.avg_duration_seconds)
      return { hours: 0, minutes: 0, seconds: 0 };

    // Convertir de milisegundos a segundos primero
    const totalSeconds = Math.floor(metric.avg_duration_seconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    return { hours, minutes, seconds };
  };

  const assignmentTime = getTimeData("pending");

  // Promedio de preparación: accepted + driver_at_branch
  const acceptedTime = getTimeData("accepted");
  const driverAtBranchTime = getTimeData("driver_at_branch");
  // Sumar correctamente los tiempos de acceptedTime y driverAtBranchTime
  const totalPrepSeconds =
    acceptedTime.hours * 3600 +
    acceptedTime.minutes * 60 +
    acceptedTime.seconds +
    driverAtBranchTime.hours * 3600 +
    driverAtBranchTime.minutes * 60 +
    driverAtBranchTime.seconds;

  const avgPrepTime = {
    hours: Math.floor(totalPrepSeconds / 3600),
    minutes: Math.floor((totalPrepSeconds % 3600) / 60),
    seconds: totalPrepSeconds % 60,
  };

  // Normalizar tiempo si segundos >= 60 o minutos >= 60
  if (avgPrepTime.seconds >= 60) {
    avgPrepTime.minutes += Math.floor(avgPrepTime.seconds / 60);
    avgPrepTime.seconds = avgPrepTime.seconds % 60;
  }
  if (avgPrepTime.minutes >= 60) {
    avgPrepTime.hours += Math.floor(avgPrepTime.minutes / 60);
    avgPrepTime.minutes = avgPrepTime.minutes % 60;
  }

  const waitingTime = getTimeData("sorted");

  // Promedio de entrega: in_transit + on_site
  const inTransitTime = getTimeData("in_transit");
  const onSiteTime = getTimeData("on_site");
  const avgDeliveryTime = {
    hours: inTransitTime.hours + onSiteTime.hours,
    minutes: inTransitTime.minutes + onSiteTime.minutes,
    seconds: inTransitTime.seconds + onSiteTime.seconds,
  };

  // Normalizar tiempo si segundos >= 60 o minutos >= 60
  if (avgDeliveryTime.seconds >= 60) {
    avgDeliveryTime.minutes += Math.floor(avgDeliveryTime.seconds / 60);
    avgDeliveryTime.seconds = avgDeliveryTime.seconds % 60;
  }
  if (avgDeliveryTime.minutes >= 60) {
    avgDeliveryTime.hours += Math.floor(avgDeliveryTime.minutes / 60);
    avgDeliveryTime.minutes = avgDeliveryTime.minutes % 60;
  }

  // Datos para el donut chart usando la configuración centralizada (memoizado)
  const donutData = useMemo(
    () => getDonutData(stateMetrics || []),
    [stateMetrics],
  );

  // Estado para el filtro de indicadores de tiempo
  const [selectedTimeFilter, setSelectedTimeFilter] = useState("all");

  // Mapeo de filtros a estados (memoizado con useCallback)
  const getStatusesForFilter = useCallback((filter: string) => {
    switch (filter) {
      case "all":
        return [
          "pending",
          "accepted",
          "driver_at_branch",
          "sorted",
          "in_transit",
          "on_site",
          "completed",
          "cancelled",
        ];
      case "pending":
        return ["pending"];
      case "assigned":
        return ["accepted"];

      case "sorted":
        return ["sorted"];
      case "in_transit":
        return ["in_transit"];
      case "on_site":
        return ["on_site"];
      case "completed":
        return ["completed"];
      case "cancelled":
        return ["cancelled"];
      default:
        return ["pending"];
    }
  }, []);

  // Calcular estadísticas de tiempo basadas en stateMetrics para el filtro seleccionado (memoizado)
  const timeStats = useMemo(() => {
    const statusesToInclude = getStatusesForFilter(selectedTimeFilter);
    // Filtrar métricas por los estados correspondientes al filtro

    const relevantMetrics = stateMetrics?.filter((metric) =>
      statusesToInclude.includes(metric.status),
    );

    if (relevantMetrics?.length === 0) {
      return {
        avgDuration: 0,
        maxDuration: 0,
        minDuration: 0,
        totalOrders: 0,
      };
    }

    // Calcular promedios si hay múltiples métricas
    const avgDuration =
      relevantMetrics?.length > 0
        ? Math.round(
            relevantMetrics.reduce(
              (sum, metric) => sum + (metric.avg_duration_seconds || 0),
              0,
            ) / relevantMetrics.length,
          )
        : 0;

    const maxDuration =
      relevantMetrics?.length > 0
        ? Math.round(
            relevantMetrics.reduce(
              (sum, metric) => sum + (metric.max_duration_seconds || 0),
              0,
            ) / relevantMetrics.length,
          )
        : 0;

    const minDuration =
      relevantMetrics?.length > 0
        ? Math.round(
            relevantMetrics.reduce(
              (sum, metric) => sum + (metric.min_duration_seconds || 0),
              0,
            ) / relevantMetrics.length,
          )
        : 0;

    // Sumar total de órdenes
    const totalOrders = relevantMetrics?.reduce(
      (sum, metric) => sum + (metric.total_orders || 0),
      0,
    );

    return {
      avgDuration,
      maxDuration,
      minDuration,
      totalOrders,
    };
  }, [stateMetrics, selectedTimeFilter, getStatusesForFilter]);

  // Opciones para el filtro
  const timeFilterOptions = [
    { value: "all", label: "Todos los estados" },
    { value: "pending", label: "En cola" },
    { value: "assigned", label: "Asignadas" },

    { value: "sorted", label: "Surtidas" },
    { value: "in_transit", label: "En tránsito" },
    { value: "on_site", label: "En sitio" },
    { value: "completed", label: "Completadas" },
    { value: "cancelled", label: "Canceladas" },
  ];

  if (loading) {
    return (
      <div className="text-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      {/* Time Metrics */}
      <div className="grid grid-cols-1 gap-3 md:gap-4 sm:grid-cols-2 xl:grid-cols-3 w-full">
        <div className="min-w-0 h-24 md:h-32">
          <TimeWidget
            hours={assignmentTime.hours}
            minutes={assignmentTime.minutes}
            seconds={assignmentTime.seconds}
            title="Promedio de asignación"
          />
        </div>
        <div className="min-w-0 h-24 md:h-32">
          <TimeWidget
            hours={avgPrepTime.hours}
            minutes={avgPrepTime.minutes}
            seconds={avgPrepTime.seconds}
            title="Promedio de preparación" // (asignadas + driver_at_branch)
          />
        </div>
        <div className="min-w-0 h-24 md:h-32 sm:col-span-2 xl:col-span-1">
          <TimeWidget
            hours={waitingTime.hours}
            minutes={waitingTime.minutes}
            seconds={waitingTime.seconds}
            title="Promedio de espera a pickup"
          />
        </div>
      </div>

      {/* Order Metrics */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 w-full">
        <div className="min-w-0 h-24 md:h-32">
          <TimeWidget
            hours={avgDeliveryTime.hours}
            minutes={avgDeliveryTime.minutes}
            seconds={avgDeliveryTime.seconds}
            title="Promedio de entrega" // (in_transit + on_site)
          />
        </div>
        <div className="min-w-0 h-24 md:h-32">
          <DashboardCard
            value={
              stateMetrics?.find((metric) => metric.status === "completed")
                ?.total_orders || 0
            }
            title="Pedidos completados"
          />
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 w-full">
        <div className="min-w-0 ">
          <DonutChart
            data={donutData}
            title="Órdenes"
            totalLabel="Órdenes"
            total={totalOrders}
          />
        </div>
        <div className="min-w-0 ">
          <OrderLineChart
            data={dailyBreakdown || []}
            title="Cantidad de órdenes entregadas"
          />
        </div>
      </div>

      {/* Time Stats Section */}
      <div className="w-full">
        <div className="bg-white p-4 rounded-xl shadow mb-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">
              Indicadores de tiempo por estado
            </h3>
            <div className="w-48">
              <Select
                value={selectedTimeFilter}
                onValueChange={setSelectedTimeFilter}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar estado" />
                </SelectTrigger>
                <SelectContent>
                  {timeFilterOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Time Stats Cards */}
        {/*
          Helper function to format seconds into "hh:mm:ss"
        */}
        {(() => {
          function formatDuration(seconds: number | undefined): string {
            if (typeof seconds !== "number" || isNaN(seconds)) return "";
            const h = Math.floor(seconds / 3600);
            const m = Math.floor((seconds % 3600) / 60);
            const s = Math.floor(seconds % 60);
            const parts = [];
            if (h > 0) parts.push(`${h}h`);
            if (m > 0 || h > 0) parts.push(`${m}m`);
            parts.push(`${s}s`);
            return parts.join(" ");
          }
          // Helper to determine color classes based on value and type
          // Define thresholds for good, mid, bad (in seconds)
          const GOOD_THRESHOLD = 1200; // 20 min
          const MID_THRESHOLD = 3600; // 60 min

          function getColorClass(value: number | undefined) {
            if (loading) return "text-gray-900";
            if (typeof value !== "number" || isNaN(value))
              return "text-gray-900";
            if (value <= GOOD_THRESHOLD) return "text-green-600";
            if (value <= MID_THRESHOLD) return "text-yellow-500";
            return "text-red-600";
          }

          function getColorBg(value: number | undefined) {
            if (loading) return "bg-gray-900";
            if (typeof value !== "number" || isNaN(value)) return "bg-gray-900";
            if (value <= GOOD_THRESHOLD) return "bg-green-600";
            if (value <= MID_THRESHOLD) return "bg-yellow-500";
            return "bg-red-600";
          }

          return (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 w-full">
              {/* Total de órdenes - PRIMERO */}
              <div className="bg-white p-4 rounded-xl shadow h-24">
                <div className="flex items-center justify-between h-full">
                  <div>
                    <p className="text-sm text-gray-500">Total de órdenes</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {loading
                        ? "..."
                        : selectedTimeFilter === "all"
                          ? totalOrders
                          : timeStats.totalOrders}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tiempo promedio - SEGUNDO */}
              <div className="bg-white p-4 rounded-xl shadow h-24">
                <div className="flex items-center justify-between h-full">
                  <div>
                    <p className="text-sm text-gray-500">Tiempo promedio</p>
                    <p
                      className={`text-2xl font-semibold ${getColorClass(timeStats.avgDuration)}`}
                    >
                      {loading ? "..." : formatDuration(timeStats.avgDuration)}
                    </p>
                  </div>
                  <div
                    className={`w-1 h-full rounded ${getColorBg(
                      timeStats.avgDuration,
                    )}`}
                  ></div>
                </div>
              </div>

              {/* Mínimo - TERCERO */}
              <div className="bg-white p-4 rounded-xl shadow h-24">
                <div className="flex items-center justify-between h-full">
                  <div>
                    <p className="text-sm text-gray-500">Mínimo</p>
                    <p
                      className={`text-2xl font-semibold ${getColorClass(timeStats.minDuration)}`}
                    >
                      {loading ? "..." : formatDuration(timeStats.minDuration)}
                    </p>
                  </div>
                  <div
                    className={`w-1 h-full rounded ${getColorBg(
                      timeStats.minDuration,
                    )}`}
                  ></div>
                </div>
              </div>

              {/* Máximo - CUARTO */}
              <div className="bg-white p-4 rounded-xl shadow h-24">
                <div className="flex items-center justify-between h-full">
                  <div>
                    <p className="text-sm text-gray-500">Máximo</p>
                    <p
                      className={`text-2xl font-semibold ${getColorClass(timeStats.maxDuration)}`}
                    >
                      {loading ? "..." : formatDuration(timeStats.maxDuration)}
                    </p>
                  </div>
                  <div
                    className={`w-1 h-full rounded ${getColorBg(
                      timeStats.maxDuration,
                    )}`}
                  ></div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default OrdersDashboard;
